import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Plus, Edit, Trash2, Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminConstructionUpdates = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    displayDate: '',
    month: '',
    year: new Date().getFullYear(),
    progress: 0,
    category: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchUpdates();
  }, [navigate]);

  const fetchUpdates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/construction-updates`);
      setUpdates(response.data);
    } catch (error) {
      console.error('Error fetching updates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    const token = localStorage.getItem('admin_token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
      toast({
        title: "Image Uploaded",
        description: "Image uploaded successfully"
      });
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const token = localStorage.getItem('admin_token');

    try {
      if (editingUpdate) {
        // Update existing
        await axios.put(
          `${BACKEND_URL}/api/admin/construction-updates/${editingUpdate._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: "Updated", description: "Construction update updated successfully" });
      } else {
        // Create new
        await axios.post(
          `${BACKEND_URL}/api/admin/construction-updates`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: "Created", description: "Construction update created successfully" });
      }

      setIsDialogOpen(false);
      resetForm();
      fetchUpdates();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save update",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (update) => {
    setEditingUpdate(update);
    setFormData({
      title: update.title,
      description: update.description,
      image_url: update.image_url,
      displayDate: update.displayDate,
      month: update.month,
      year: update.year,
      progress: update.progress,
      category: update.category
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (updateId) => {
    if (!window.confirm('Are you sure you want to delete this update?')) return;

    const token = localStorage.getItem('admin_token');

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/construction-updates/${updateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Deleted", description: "Construction update deleted successfully" });
      fetchUpdates();
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: error.response?.data?.detail || "Failed to delete update",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      displayDate: '',
      month: '',
      year: new Date().getFullYear(),
      progress: 0,
      category: ''
    });
    setEditingUpdate(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Construction Updates</h1>
                <p className="text-sm text-gray-600">Total: {updates.length}</p>
              </div>
            </div>
            <Button 
              onClick={() => {
                resetForm();
                setIsDialogOpen(true);
              }}
              className="bg-gradient-to-r from-orange-500 to-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Update
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              if (!open) {
                handleDialogClose();
              }
            }}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto z-50">
                <DialogHeader>
                  <DialogTitle>
                    {editingUpdate ? 'Edit Construction Update' : 'Add New Construction Update'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Title *</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                      placeholder="e.g., 15th Floor Structure Completed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Description *</label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      required
                      rows={4}
                      placeholder="Detailed description of the construction milestone..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Image</label>
                    <div className="space-y-2">
                      {formData.image_url && (
                        <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={formData.image_url.startsWith('/') ? formData.image_url : formData.image_url}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex space-x-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="flex-1"
                        />
                        {uploadingImage && <Loader2 className="w-5 h-5 animate-spin" />}
                      </div>
                      <Input
                        value={formData.image_url}
                        onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                        placeholder="Or paste image URL"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Display Date * (e.g., January 15, 2025)</label>
                      <Input
                        value={formData.displayDate}
                        onChange={(e) => setFormData({...formData, displayDate: e.target.value})}
                        required
                        placeholder="January 15, 2025"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Month *</label>
                      <Input
                        value={formData.month}
                        onChange={(e) => setFormData({...formData, month: e.target.value})}
                        required
                        placeholder="January"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Year *</label>
                      <Input
                        type="number"
                        value={formData.year}
                        onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Progress % *</label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.progress}
                        onChange={(e) => setFormData({...formData, progress: parseInt(e.target.value)})}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Category *</label>
                      <Input
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        required
                        placeholder="Structural Work"
                      />
                    </div>
                  </div>

                  <div className="flex space-x-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDialogClose}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600"
                    >
                      {isSubmitting ? 'Saving...' : (editingUpdate ? 'Update' : 'Create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : updates.length === 0 ? (
          <Card className="p-12 text-center">
            <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No construction updates yet</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {updates.map((update) => (
              <Card key={update._id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative h-48">
                  <img
                    src={update.image_url.startsWith('/') ? update.image_url : update.image_url}
                    alt={update.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {update.progress}%
                  </div>
                </div>
                <div className="p-4">
                  <div className="mb-2">
                    <span className="text-xs text-orange-600 font-semibold">{update.category}</span>
                    <h3 className="font-bold text-lg text-gray-900 mt-1">{update.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">{update.description}</p>
                  <p className="text-xs text-gray-500 mb-4">{update.displayDate}</p>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(update)}
                      className="flex-1"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(update._id)}
                      className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminConstructionUpdates;
